import {
  Box,
  CardContent,
  Divider,
  FormControl,
  FormLabel,
  Stack,
} from '@mui/material';
import { Provider } from '@repo/api-gw-sdk';

import { AvailabilityZoneSelect } from '@/components/regions/availabilityZoneSelect';
import { GcpMachineTypeSelect } from '@/components/regions/gcpMachineTypeSelect';
import { GcpSubnetSelect } from '@/components/regions/gcpSubnetSelect';
import { RestoreRegionSelect } from '@/components/regions/restoreRegionSelect';
import { TransactionalTextField } from '@/components/shared/transactionalTextField';
import { SnapshotToolbar } from '@/components/snapshots/snapshotToolbar';
import { TagsSection } from '@/components/tags/tagsSection';
import type { StepProps } from '@/components/wizard/StepProps';

import { StepContainer } from '../../../wizard/StepContainer';
import type { RestoreGcpVMState } from '../../restoreGcpVMWizard';

export const GcpVmConfiguration = (props: StepProps<RestoreGcpVMState>) => {
  const onNextClick = async () => {
    props.setNextStep(props.currentStep.next?.[0]);
  };

  return (
    <StepContainer
      sx={{ padding: '0' }}
      stepperLabels={props.stepperLabels}
      stepperIndex={1}
      onBackClick={props.back}
      canGoNext={() =>
        !!props.wizardState.vmName &&
        !!props.wizardState.subnetName &&
        !!props.wizardState.regionName &&
        !!props.wizardState.machineType &&
        !!props.wizardState.zone
      }
      onNextClick={onNextClick}
      toolbarComponent={
        <SnapshotToolbar
          pointInTime={props.wizardState.snapshot.pointInTime}
          vaultId={props.wizardState.snapshot.vaultId}
          accessDeniedItemsCount={
            props.wizardState.snapshot.accessDeniedItemsCount
          }
        />
      }
    >
      <Box className='mx-[40px] my-[24px]'>
        <Stack direction='row' gap='24px' className='mt-[20px] mb-[24px]'>
          <FormControl size='small' className='flex-1'>
            <FormLabel>VM Name*</FormLabel>
            <TransactionalTextField
              initValue={props.wizardState.vmName}
              onChange={(vmName) =>
                props.setWizardState((state) => ({ ...state, vmName }))
              }
            />
          </FormControl>
        </Stack>
        <Stack direction='row' gap='24px' className='mt-[20px] mb-[24px]'>
          <RestoreRegionSelect
            cloudProvider={Provider.Gcp}
            restoreAccountId={props.wizardState.restoreAccount?.id}
            regionName={props.wizardState.regionName}
            onChange={(regionName) =>
              props.setWizardState((state) => ({
                ...state,
                zone: undefined,
                subnetName: undefined,
                subnetId: undefined,
                networkName: undefined,
                networkHostProject: undefined,
                regionName,
              }))
            }
          />
          <AvailabilityZoneSelect
            accountId={props.wizardState.restoreAccount?.id}
            regionName={props.wizardState.regionName}
            availabilityZone={props.wizardState.zone}
            cloudProvider={Provider.Gcp}
            onChange={(zone) =>
              props.setWizardState((state) => ({
                ...state,
                zone,
              }))
            }
          />
        </Stack>
        <Stack direction='row' gap='24px' className='mt-[20px] mb-[24px]'>
          <GcpSubnetSelect
            accountId={props.wizardState.restoreAccount?.id}
            region={props.wizardState.regionName}
            subnetId={props.wizardState.subnetId}
            onChange={(subnetName, subnetId, networkName, networkHostProject) =>
              props.setWizardState((state) => ({
                ...state,
                subnetName: subnetName,
                subnetId: subnetId,
                networkName: networkName,
                networkHostProject: networkHostProject,
              }))
            }
          />
          <GcpMachineTypeSelect
            machineType={props.wizardState.machineType}
            accountId={props.wizardState.restoreAccount?.id}
            zone={props.wizardState.zone}
            onChange={(machineType) =>
              props.setWizardState((state) => ({
                ...state,
                machineType: machineType || '',
              }))
            }
          />
        </Stack>
      </Box>
      <Divider />
      <CardContent className='px-0'>
        <TagsSection
          initialTags={props.wizardState.initialTags ?? {}}
          tags={props.wizardState.tags ?? {}}
          keepOriginalTags={props.wizardState.keepOriginalTags}
          setKeepOriginalTags={(keepOriginalTags) =>
            props.setWizardState((state) => ({ ...state, keepOriginalTags }))
          }
          onChange={(tags) =>
            props.setWizardState((state) => ({ ...state, tags }))
          }
          provider={Provider.Gcp}
        />
      </CardContent>
    </StepContainer>
  );
};
